import { CheckIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ComboBox } from "~/components/common/combo-box";
import { Button } from "~/components/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import type {
  ActionEventType,
  ActionResource,
  ActionResourceType,
} from "~/types/api";
import type { FormOption } from "~/types/ui";
import {
  ActionEventTypeEnum,
  getActionResourceType,
} from "~/utils/action-util";

export default function ActionResources({
  value,
  onChange,
  options,
  eventType,
}: {
  value: ActionResource[];
  onChange: (val: ActionResource[]) => void;
  options: {
    label: string;
    value: any[];
  }[];
  eventType: ActionEventType;
}) {
  const { t } = useTranslation();

  const filteredOptions = useMemo(() => {
    switch (eventType) {
      case ActionEventTypeEnum.NODE_OFFLINE:
      case ActionEventTypeEnum.DATA_INGEST_ERROR:
        return options?.find((x) => x.label == "nodes")?.value ?? [];

      case ActionEventTypeEnum.ALARM_ACKNOWLEDGED:
      case ActionEventTypeEnum.ALARM_RAISED:
        return options?.find((x) => x.label == "alarms")?.value ?? [];

      case ActionEventTypeEnum.GATEWAY_OFFLINE:
        return options?.find((x) => x.label == "gateways")?.value ?? [];

      default:
        return (
          options
            ?.find((x) => x.label == "actions")
            ?.value?.filter((x) => x.actionEventType == eventType) ?? []
        );
    }
  }, [eventType, options]);

  function handleChange(ids: string | string[]) {
    ids = ids as string[];
    const resourceType = getActionResourceType(eventType) as ActionResourceType;
    const newVal = [] as ActionResource[];

    for (var id of ids) {
      newVal.push(
        value.find((x) => x.identifier == id) ??
          ({
            identifier: id,
            resourceType: resourceType,
          } as ActionResource),
      );
    }
    onChange(newVal);
  }

  return (
    <div className="flex w-auto ">
      <div className="w-full">
        <ComboBox
          options={filteredOptions as FormOption<string>[]}
          value={value.map((x) => x.identifier ?? "")}
          onChange={handleChange}
          placeholder={t("actions.placeholders.resources")}
          isMulti
          clearable
        />
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleChange(filteredOptions.map((x) => x.value))}
          >
            <CheckIcon className="h-[1.2rem] w-[1.2rem] text-green-600" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("common.selectAll")}</TooltipContent>
      </Tooltip>
    </div>
  );
}
